import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import Seo, { SITIO } from "../components/Seo.jsx";
import ToolCard from "../components/ToolCard.jsx";
import AdSlot, { RANURAS } from "../components/AdSlot.jsx";
import { TOOLS, CATEGORIES } from "../data/tools.js";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "tu-kit.com",
  url: SITIO,
  inLanguage: "es-MX",
  description: "Calculadoras y herramientas financieras gratuitas para México.",
};

export default function Home() {
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("Todos");
  const campoBusqueda = useRef(null);

  // "/" enfoca la búsqueda, Esc la limpia. Cuesta poco y quien usa el
  // sitio a diario lo agradece.
  useEffect(() => {
    function alTeclear(e) {
      const enCampo = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === "/" && !enCampo) {
        e.preventDefault();
        campoBusqueda.current?.focus();
      } else if (e.key === "Escape" && e.target === campoBusqueda.current) {
        setBusqueda("");
        campoBusqueda.current.blur();
      }
    }
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, []);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return TOOLS.filter((t) => {
      if (categoria !== "Todos" && t.category !== categoria) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.shortDescription.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.seo.keywords ?? []).some((k) => k.includes(q))
      );
    });
  }, [busqueda, categoria]);

  const filtrando = busqueda.trim() !== "" || categoria !== "Todos";

  // Sin filtro se agrupa por categoría: se escanea mejor y cada
  // encabezado es un enlace interno más hacia las herramientas.
  const porCategoria = useMemo(
    () => CATEGORIES.map((c) => [c, visibles.filter((t) => t.category === c)]).filter(([, l]) => l.length),
    [visibles],
  );

  return (
    <>
      <Seo
        title="tu-kit.com — Calculadoras financieras gratuitas para México"
        description="Calculadoras de IVA, sueldo neto IMSS/ISR, crédito INFONAVIT, interés compuesto, divisas y más. Gratis, sin registro y con datos fiscales 2026."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Encabezado contenido a propósito: quien llega viene a calcular
          algo, no a leer una portada. La rejilla debe verse casi sin
          hacer scroll. */}
      <section className="grid items-end gap-8 pt-8 pb-1 md:pt-14 lg:grid-cols-[1fr_20rem]">
        <div>
          <h1 className="max-w-3xl text-[2.5rem] leading-[1.02] font-extrabold tracking-tight md:text-[4rem]">
            Haz la cuenta.
            <span className="acento block">Sin registro, sin vueltas.</span>
          </h1>
          <p className="texto-2 mt-4 max-w-xl text-base leading-relaxed">
            {TOOLS.length - 1} calculadoras de impuestos, sueldo, crédito y obra, con las tasas de México
            vigentes. Todo se calcula en tu navegador.
          </p>

          <div className="relative mt-6 max-w-lg">
            <span className="texto-3 pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <Search className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <input
              ref={campoBusqueda}
              id="search-tools"
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Busca: IVA, sueldo, obra…"
              aria-label="Buscar calculadoras"
              className="campo py-3.5 pl-11 pr-12 text-base"
            />
            {busqueda ? (
              <button
                type="button"
                onClick={() => setBusqueda("")}
                aria-label="Limpiar búsqueda"
                className="texto-3 absolute inset-y-0 right-0 flex items-center px-4 transition hover:text-[color:var(--texto)]"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="borde texto-3 pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border px-1.5 py-0.5 font-mono text-2xs sm:block">
                /
              </kbd>
            )}
          </div>
        </div>

        {/* Muestra del ticket: es decorativo y enseña qué entrega el sitio. */}
        <div aria-hidden="true" className="ticket hidden rotate-1 lg:block">
          <span className="block text-2xs font-bold uppercase tracking-[0.1em] opacity-70">IVA 16% sobre $1,000</span>
          <strong className="ticket-cifra cifra mt-2 block font-mono text-[2.5rem] leading-none font-bold">$1,160.00</strong>
          <span className="mt-3 block text-xs opacity-75">Subtotal $1,000 · IVA $160</span>
        </div>
      </section>

      <nav aria-label="Categorías" className="scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {["Todos", ...CATEGORIES].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategoria(c)}
            aria-pressed={categoria === c}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition active:translate-y-px ${
              categoria === c
                ? "border-transparent bg-[color:var(--acento)] text-white"
                : "borde texto-2 hover:border-[color:var(--color-marca-300)] hover:text-[color:var(--acento-texto)]"
            }`}
          >
            {c}
          </button>
        ))}
      </nav>

      {visibles.length === 0 ? (
        <div className="tarjeta px-6 py-16 text-center">
          <p className="font-semibold">Nada coincide con «{busqueda}»</p>
          <p className="texto-2 mx-auto mt-2 max-w-sm text-sm">
            Prueba con otra palabra, o dinos qué calculadora te hace falta y la construimos.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => { setBusqueda(""); setCategoria("Todos"); }} className="boton boton-suave">
              Ver todas
            </button>
            <Link to="/proponer-herramienta" className="boton boton-primario">
              Proponer una herramienta
            </Link>
          </div>
        </div>
      ) : filtrando ? (
        <div className="borde border-b">
          {visibles.map((t, i) => <ToolCard key={t.id} tool={t} n={i} />)}
        </div>
      ) : (
        <div className="space-y-10">
          {porCategoria.map(([cat, lista], i) => (
            <section key={cat} aria-labelledby={`cat-${cat}`} className="grid gap-2 md:grid-cols-[11rem_1fr] md:gap-8">
              <h2 id={`cat-${cat}`} className="text-sm font-bold uppercase tracking-[0.08em] md:pt-5">
                {cat}
                <span className="texto-3 cifra ml-2 font-mono font-normal">{String(lista.length).padStart(2, "0")}</span>
              </h2>
              <div>
                <div className="borde border-b">
                  {lista.map((t, j) => <ToolCard key={t.id} tool={t} n={j} />)}
                </div>
                {i === 0 && <AdSlot slot={RANURAS.listado} className="mt-8" />}
              </div>
            </section>
          ))}
        </div>
      )}

      <section className="prosa mt-6">
        <h2 className="mb-3 text-xl font-bold text-[color:var(--texto)]">
          Por qué otra página de calculadoras
        </h2>
        <p>
          Porque casi todas las que encuentras están pensadas para otro país. Usan otra tasa de
          impuesto, otro salario mínimo y otras reglas laborales, así que el número que te dan no
          sirve para tu recibo de nómina ni para tu declaración.
        </p>
        <p className="mt-3">
          Estas parten de la normativa mexicana: la tarifa del ISR que publica el SAT, las cuotas
          obrero del IMSS, los días de vacaciones que fija la Ley Federal del Trabajo tras la reforma
          de 2023 y las tasas que el INFONAVIT aplica según tu nivel de ingreso. Cada herramienta
          indica de cuándo son los datos que usa, para que sepas si sigue al día.
        </p>
        <p className="mt-3">
          No hay registro, no hay que instalar nada y lo que escribes no sale de tu navegador.
        </p>
      </section>
    </>
  );
}
